// src/App.tsx

import React, { useEffect } from 'react';
import './styles/theme.css';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation
} from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { CartProvider } from './contexts/CartContext';
import LoginPage from './pages/LoginPage';
import SubdecksPage from './pages/SubdecksPage';
import EditorPage from './pages/EditorPage';
import StudyPage from './pages/StudyPage';
import ViewPage from './pages/ViewPage';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardPage from './pages/DashboardPage';
import CycleDecksPage from './pages/CycleDecksPage';
import AdminPanelPage from './pages/AdminPanelPage';
import { ClipboardProvider } from './contexts/ClipboardContext';
import AddCardsPage from './pages/AddCardsPage';
import ResumoReadPage from './pages/ResumoReadPage';
import ResumoEditorPage from './pages/ResumoEditorPage';
import GroupDetailPage from './pages/GroupDetailPage';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import MarketplacePage from './pages/MarketplacePage';
import ProductDetailPage from './pages/ProductDetailPage';
import CheckoutPage from './pages/CheckoutPageNew';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentFailurePage from './pages/PaymentFailurePage';
import PaymentPendingPage from './pages/Paymentpendingpage';
import LandingPage from './pages/LandingPage';
import SignupPage from './pages/SignupPage';
import VendorDashboard from './pages/VendorDashboard';
import CartBanner from './components/CartBanner';
import AdminTestJobs from './pages/AdminTestJobs';
import StudentAreaPage from './pages/StudentAreaPage';
import { useActivityTracking } from './hooks/useActivityTracking';
import TermsPage from './pages/TermsPage';
import SessionsPage from './pages/SessionsPage';
import MFASetupPage from './pages/MFASetupPage';
import MFAVerifyPage from './pages/MFAVerifyPage';
import SimuladoPage from './pages/SimuladoPage';
import ProvaPage from './pages/ProvaPage';

import GoogleCallbackPage from './pages/GoogleCallbackPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

import PreviewGalleryPage from './pages/PreviewGalleryPage';
import PreviewDeckPage from './pages/PreviewDeckPage';
import PreviewSubdeckPage from './pages/PreviewSubdeckPage';
import ApostilaPreviewPage from './pages/ApostilaPreviewPage';
import PublicProductPage from './pages/PublicProductPage';

import { useState, useCallback, useRef } from 'react';
import { useSessionHeartbeat } from './hooks/useSessionHeartbeat';
import SessionRevokedModal from './components/SessionRevokedModal';

import { StudyTimerProvider } from './contexts/StudyTimerContext';
import FloatingTimer from './components/timer/FloatingTimer';
import FullScreenTimer from './components/timer/FullScreenTimer';
import StopTimerModal from './components/timer/StopTimerModal';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { FeaturesProvider } from './contexts/FeaturesContext';
import InvitePage from './pages/InvitePage';
import MyFlashcardsPage from './user-creator/pages/MyFlashcardsPage';
import UserDeckPage from './user-creator/pages/UserDeckPage';
import MaintenanceNotice from './components/MaintenanceNotice';

import RyanLoginPage from './ryan/pages/RyanLoginPage';
import RyanTermsPage from './ryan/pages/RyanTermsPage';
import RyanInvitePage from './ryan/pages/RyanInvitePage';
import RyanHomePage from './ryan/pages/RyanHomePage';
import RyanProtectedRoute from './ryan/components/RyanProtectedRoute';
import { RyanAuthProvider } from './ryan/context/RyanAuthContext';
import RyanFieldPage from './ryan/pages/RyanFieldPage';
import RyanAdminPage from './ryan/admin/RyanAdminPage';
import RyanStatsPage from './ryan/pages/RyanStatsPage';
import RyanProductPage from './ryan/pages/RyanProductPage';
import RyanCheckoutPage from './ryan/pages/RyanCheckoutPage';
import RyanStudentAreaPage from './ryan/pages/RyanStudentAreaPage';
import RyanAmbassadorPage from './ryan/pages/RyanAmbassadorPage';
import RyanResetPasswordPage from './ryan/pages/RyanResetPasswordPage';
import RyanFlashcardPreviewPage from './ryan/pages/RyanFlashcardPreviewPage';
import GiftListPage from './Casamento/GiovanaELucas/GiftListPage';

// ✅ CRIAR COMPONENTE WRAPPER
const PendingPurchaseChecker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // ✅ Verificar se há compra pendente
    const pending = localStorage.getItem('pendingPurchase');

    if (pending && location.pathname !== '/marketplace/success') {
      try {
        const data = JSON.parse(pending);
        const now = Date.now();
        const elapsed = now - data.timestamp;

        // Se passou menos de 1 hora (3600000ms)
        if (elapsed < 3600000) {
          console.log('🔍 Compra pendente detectada, redirecionando...');
          localStorage.removeItem('pendingPurchase');
          navigate('/marketplace/success');
        }
      } catch (error) {
        console.error('Erro ao processar compra pendente:', error);
        localStorage.removeItem('pendingPurchase');
      }
    }
  }, [navigate, location]);

  return <>{children}</>;
};

// ✅ Componente que força tema light em rotas públicas e aplica classe de plataforma
const ThemeController: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    const publicRoutes = ['/', '/login', '/cadastro', '/marketplace', '/checkout'];
    const isPublicRoute = publicRoutes.some(route =>
      location.pathname === route ||
      (location.pathname.startsWith('/marketplace/') && !location.pathname.includes('success'))
    ) || location.pathname.startsWith('/preview') || location.pathname.startsWith('/previa/') ||
        location.pathname.startsWith('/produto/');

    // Aplica identidade visual por plataforma.
    // Rotas /ryan/* recebem theme-ryan diretamente; páginas compartilhadas
    // (/study, /view, /editor) navegadas a partir do Ryan chegam com
    // location.state.fromRyan === true ou com sessionStorage de contexto
    // (necessário para sobreviver a refresh de página). /previa/:urlSlug
    // (prévia pública de apostila) também força o tema Ryan — na prática só
    // é usada pelo fluxo de vendas da GRAIA Medicina (ver
    // RyanApostilaPreviewModal.tsx); embora a rota de geração do link no
    // backend seja genérica, hoje não existe UI fora do Ryan que a exponha.
    const isRyanContext =
      location.pathname.startsWith('/ryan') ||
      location.pathname.startsWith('/previa') ||
      !!(location.state as any)?.fromRyan ||
      sessionStorage.getItem('graia_theme_context') === 'ryan';
    if (isRyanContext) {
      document.body.classList.add('theme-ryan');
    } else {
      document.body.classList.remove('theme-ryan');
    }

    // O StudyPage é a ÚNICA tela da plataforma Ryan com modo escuro
    // configurado — o resto (dashboard, RyanFieldPage, etc.) não tem estilos
    // dark e fica com cores do ENEM vazando (azul) quando o dark mode do
    // StudyPage "escapa". O cleanup do StudyPage já desliga o dark ao sair
    // normalmente, mas se o usuário fechar a aba/navegador sem navegar para
    // fora (ou o processo simplesmente morrer), o valor 'dark' persiste no
    // localStorage e volta a ser aplicado no próximo login, direto numa
    // página sem suporte a dark. Este guard cobre esse caso: em qualquer
    // rota Ryan que não seja o StudyPage, força e PERSISTE o tema como
    // light — não só no atributo do DOM, mas no estado (senão a próxima
    // navegação reaplicaria o 'dark' salvo).
    const isRyanDarkAllowedRoute = location.pathname.startsWith('/study');
    if (isRyanContext && !isRyanDarkAllowedRoute && theme === 'dark') {
      setTheme('light');
      document.body.setAttribute('data-theme', 'light');
    } else if (isPublicRoute) {
      document.body.setAttribute('data-theme', 'light');
    } else {
      document.body.setAttribute('data-theme', theme);
    }

    // Favicon por plataforma: /ryan/* usa a logo própria da GRAIA Medicina,
    // o restante do site mantém o favicon padrão da GRAIA ENEM.
    // Recria o <link> em vez de só trocar o href — mutar href in-place é
    // inconsistente entre navegadores/versões pra forçar o repaint do ícone
    // na aba (mesmo problema de cache já documentado acima para o
    // apple-touch-icon do PWA do RYAN no Safari).
    const desiredHref = isRyanContext ? '/Ryan/graia-logo-aba.png' : '/logo-icon.png';
    const faviconLink = document.getElementById('app-favicon') as HTMLLinkElement | null;
    if (faviconLink && faviconLink.getAttribute('href') !== desiredHref) {
      const newFavicon = document.createElement('link');
      newFavicon.rel = 'icon';
      newFavicon.id = 'app-favicon';
      newFavicon.type = 'image/png';
      newFavicon.href = desiredHref;
      faviconLink.parentNode?.replaceChild(newFavicon, faviconLink);
    }
  }, [location.pathname, location.state, theme, setTheme]);

  return <>{children}</>;
};

// Garante redirecionamentos por hostname em navegações client-side subsequentes
// (o carregamento inicial já é tratado de forma síncrona em index.tsx).
const HostnameRedirector: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const host = window.location.hostname;

  // Páginas compartilhadas (/study, /view, /resumo, /editor) são navegadas a partir
  // do Ryan SEM prefixo /ryan, carregando o contexto via location.state.fromRyan ou
  // sessionStorage (mesmo sinal usado pelo ThemeController acima) — não são "fora do
  // Ryan" e não devem ser forçadas de volta para /ryan/<path> (o que não bate com
  // nenhuma rota registrada e cai no catch-all /ryan/*, mandando o usuário pra Home).
  const isRyanContext =
    location.pathname.startsWith('/ryan') ||
    !!(location.state as any)?.fromRyan ||
    sessionStorage.getItem('graia_theme_context') === 'ryan';

  // /previa/:urlSlug é rota pública compartilhada (prévia de apostila gerada
  // em ResumoEditorPage.tsx, acessível sem login) — nunca tem prefixo /ryan
  // nem contexto prévio, e não deve ser empurrada pra /ryan/previa/..., que
  // não bate com nenhuma rota registrada e cai no catch-all /ryan/*
  // (mandava quem clicou o link de prévia direto pra Home do Ryan).
  const isPublicSharedRoute = location.pathname.startsWith('/previa/');

  // Renderização síncrona: se por qualquer motivo o path não foi reescrito
  // (navegação interna via <Link> ou pushState), redireciona imediatamente
  // antes de renderizar qualquer filho.
  if (host.startsWith('med.') && !isRyanContext && !isPublicSharedRoute) {
    const rest = location.pathname === '/' ? '' : location.pathname;
    return <Navigate to={'/ryan' + rest} replace />;
  }

  if (host.startsWith('lucasegiovana.') && (location.pathname === '/' || location.pathname === '')) {
    return <Navigate to="/lista-presentes" replace />;
  }

  return <>{children}</>;
};

// ✅ NOVO: Componente interno com acesso ao AuthContext
const AppContent: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sessionRevoked, setSessionRevoked] = useState(false);
  // Capturado no exato momento da revogação (ver handleSessionRevoked) — não
  // dá pra reler location/sessionStorage só na hora de fechar o modal: assim
  // que a sessão cai, ProtectedRoute redireciona pra "/" na hora (isAuthenticated
  // vira false), então por enquanto o modal fica aberto o location/tema já
  // pode ter voltado a ser o da ENEM por baixo dele. Guardando o contexto
  // aqui, o modal sempre mostra a identidade certa e "Fazer login novamente"
  // sempre manda pro login certo, não importa o que aconteceu com a rota
  // enquanto a pessoa ainda não clicou.
  const [sessionRevokedIsRyan, setSessionRevokedIsRyan] = useState(false);

  // Ref em vez de colocar `location` direto nas deps do useCallback abaixo:
  // handleSessionRevoked precisa ficar com identidade ESTÁVEL, senão toda
  // navegação reinicia o setInterval do heartbeat (ver useSessionHeartbeat —
  // checkSession depende de onSessionRevoked), adiando o próximo check pra
  // "30s depois da última navegação" em vez de um intervalo fixo de verdade.
  const locationRef = useRef(location);
  useEffect(() => { locationRef.current = location; }, [location]);

  const handleSessionRevoked = useCallback(() => {
    const loc = locationRef.current;
    setSessionRevokedIsRyan(
      loc.pathname.startsWith('/ryan') ||
      !!(loc.state as any)?.fromRyan ||
      sessionStorage.getItem('graia_theme_context') === 'ryan'
    );
    setSessionRevoked(true);
  }, []);

  useSessionHeartbeat({
    intervalMs: 30000, // checar a cada 30s
    onSessionRevoked: handleSessionRevoked,
  });

  const handleModalClose = useCallback(() => {
    setSessionRevoked(false);
    navigate(sessionRevokedIsRyan ? '/ryan/login' : '/login');
  }, [navigate, sessionRevokedIsRyan]);

  useActivityTracking({
    enabled: true,
    isAdmin: isAdmin || false,
    userId: (user as any)?._id || undefined,
  });

  return (
    <StudyTimerProvider>
      {sessionRevoked && <SessionRevokedModal onClose={handleModalClose} isRyan={sessionRevokedIsRyan} />}
      <CartBanner />

      {/* ✅ Componentes globais do timer */}
      <FloatingTimer />
      <FullScreenTimer />
      <StopTimerModal />

      <HostnameRedirector>
      <ThemeController>
        <PendingPurchaseChecker>
          <Routes>
            {/* Rotas Públicas */}
            <Route path="/" element={<MaintenanceNotice><LandingPage /></MaintenanceNotice>} />
            <Route path="/login" element={<MaintenanceNotice><LoginPage /></MaintenanceNotice>} />
            <Route path="/cadastro" element={<SignupPage />} />
            <Route path="/termos" element={<TermsPage />} />
            <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
            <Route path="/sessions" element={<SessionsPage />} />

            {/* Marketplace (público) */}
            <Route path="/marketplace" element={<MarketplacePage />} />
            <Route path="/marketplace/:productId" element={<ProductDetailPage />} />

            {/* Página pública de venda de produto */}
            <Route path="/produto/:slug" element={<PublicProductPage />} />

            <Route path="/checkout" element={<CheckoutPage />} />

            <Route path="/marketplace/failure" element={<ProtectedRoute><PaymentFailurePage /></ProtectedRoute>} />
            <Route path="/marketplace/pending" element={<ProtectedRoute><PaymentPendingPage /></ProtectedRoute>} />

            <Route path="/vendor" element={<ProtectedRoute><VendorDashboard /></ProtectedRoute>} />

            <Route
              path="/marketplace/success"
              element={<PaymentSuccessPage />}
            />

            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              }
            />


            <Route path="/mfa/setup" element={
              <ProtectedRoute>
                <MFASetupPage />
              </ProtectedRoute>
            } />

            <Route path="/mfa/verify" element={<MFAVerifyPage />} />

            {/* Dashboard como página inicial após login */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } />

            {/* Área do Aluno */}
            <Route path="/student-area" element={
              <ProtectedRoute>
                <StudentAreaPage />
              </ProtectedRoute>
            } />

            {/* Recuperação de Senha */}
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Página de decks do ciclo */}
            <Route path="/cycles/:cycleId/decks" element={
              <ProtectedRoute>
                <CycleDecksPage />
              </ProtectedRoute>
            } />

            {/* Página de Detalhes do Grupo */}
            <Route path="/groups/:groupId" element={
              <ProtectedRoute>
                <GroupDetailPage />
              </ProtectedRoute>
            } />

            <Route path="/invite/:code" element={<InvitePage />} />

            {/* Teste de Jobs (Admin) */}
            <Route path="/admin/test-jobs" element={
              <ProtectedRoute>
                <AdminTestJobs />
              </ProtectedRoute>
            } />

            <Route path="/simulado/:simuladoId" element={<SimuladoPage />} />
            <Route path="/prova/:examId" element={<ProvaPage />} />

            {/* ========== DECKS OFICIAIS ========== */}

            {/* Subdecks */}
            <Route path="/subdecks/:deckId" element={
              <ProtectedRoute>
                <SubdecksPage />
              </ProtectedRoute>
            } />

            {/* Estudo */}
            <Route path="/study/:deckId/:subdeckId" element={
              <ProtectedRoute>
                <StudyPage />
              </ProtectedRoute>
            } />

            {/* Visualizar cards */}
            <Route path="/view/:deckId/:subdeckId" element={
              <ProtectedRoute>
                <ViewPage />
              </ProtectedRoute>
            } />

            {/* Editor de cards */}
            <Route path="/editor/:deckId/:subdeckId" element={
              <ProtectedRoute>
                <EditorPage />
              </ProtectedRoute>
            } />

            {/* ✅ NOVO: Resumo - Leitura (Deck Oficial) */}
            <Route path="/resumo/:deckId/:subdeckId" element={
              <ProtectedRoute>
                <ResumoReadPage />
              </ProtectedRoute>
            } />

            {/* ✅ NOVO: Resumo - Edição (Deck Oficial, Admin) */}
            <Route path="/resumo/:deckId/:subdeckId/edit" element={
              <ProtectedRoute>
                <ResumoEditorPage />
              </ProtectedRoute>
            } />

            {/* ✅ PÁGINA ADICIONAR CARDS (ÚNICA ROTA) */}
            <Route path="/add-cards/:deckId/:subdeckId" element={
              <ProtectedRoute>
                <AddCardsPage />
              </ProtectedRoute>
            } />

            {/* ========== OUTRAS ROTAS ========== */}

            {/* Painel de Administração */}
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminPanelPage />
              </ProtectedRoute>
            } />

            {/* Estudo de flashcards salvos */}
            <Route path="/study/saved" element={
              <ProtectedRoute>
                <StudyPage />
              </ProtectedRoute>
            } />

            {/* Estudo personalizado */}
            <Route path="/study/custom" element={
              <ProtectedRoute>
                <StudyPage />
              </ProtectedRoute>
            } />

            {/* ========== CRIADOR DE FLASHCARDS DO USUÁRIO ========== */}
            <Route path="/my-flashcards" element={<ProtectedRoute><MyFlashcardsPage /></ProtectedRoute>} />
            <Route path="/my-flashcards/:collectionId" element={<ProtectedRoute><UserDeckPage /></ProtectedRoute>} />
            <Route path="/my-flashcards/editor/:deckId" element={<ProtectedRoute><EditorPage isUserDeck={true} /></ProtectedRoute>} />
            <Route path="/study/user/:deckId" element={<ProtectedRoute><StudyPage isUserContent={true} /></ProtectedRoute>} />
            {/* Vitrine Pública (sem autenticação) */}
            <Route path="/preview" element={<PreviewGalleryPage />} />
            <Route path="/preview/:deckSlug" element={<PreviewDeckPage />} />
            <Route path="/preview/:deckSlug/:subdeckSlug" element={<PreviewSubdeckPage />} />

            {/* Prévia por link temporário (sem autenticação) */}
            <Route path="/previa/:urlSlug" element={<ApostilaPreviewPage />} />

            {/* Prévia de flashcards Ryan (pública, sem autenticação) */}
            <Route path="/ryan/previa-cards/:urlSlug" element={<RyanFlashcardPreviewPage />} />

            {/* ========== CASAMENTO — LISTA DE PRESENTES ========== */}
            <Route path="/lista-presentes" element={<GiftListPage />} />

            {/* ========== ROTAS RYAN ========== */}
            <Route path="/ryan/admin" element={<RyanAdminPage />} />
            <Route
              path="/ryan/estatisticas"
              element={
                <RyanAuthProvider>
                  <RyanProtectedRoute>
                    <RyanStatsPage />
                  </RyanProtectedRoute>
                </RyanAuthProvider>
              }
            />
            <Route path="/ryan/login" element={<RyanLoginPage />} />
            <Route path="/ryan/termos" element={<RyanTermsPage />} />
            <Route
              path="/ryan/campo/:spaceId"
              element={
                <RyanAuthProvider>
                  <RyanProtectedRoute>
                    <RyanFieldPage />
                  </RyanProtectedRoute>
                </RyanAuthProvider>
              }
            />
            <Route path="/ryan/produto/:slug" element={<RyanProductPage />} />
            <Route path="/ryan/checkout/:slug" element={<RyanCheckoutPage />} />
            <Route path="/ryan/convite/:token" element={<RyanInvitePage />} />
            <Route path="/ryan/convite" element={<RyanInvitePage />} />
            <Route path="/ryan/reset-password/:token" element={<RyanResetPasswordPage />} />
            <Route
              path="/ryan/minha-area"
              element={
                <RyanAuthProvider>
                  <RyanProtectedRoute>
                    <RyanStudentAreaPage />
                  </RyanProtectedRoute>
                </RyanAuthProvider>
              }
            />
            <Route
              path="/ryan/embaixador"
              element={
                <RyanAuthProvider>
                  <RyanProtectedRoute>
                    <RyanAmbassadorPage />
                  </RyanProtectedRoute>
                </RyanAuthProvider>
              }
            />
            <Route
              path="/ryan/*"
              element={
                <RyanAuthProvider>
                  <RyanProtectedRoute>
                    <RyanHomePage />
                  </RyanProtectedRoute>
                </RyanAuthProvider>
              }
            />

            {/* Rota catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </PendingPurchaseChecker>
      </ThemeController>
      </HostnameRedirector>
    </StudyTimerProvider>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,      // dados ficam "frescos" por 5 minutos
      gcTime: 1000 * 60 * 10,         // cache fica na memória por 10 minutos
      refetchOnWindowFocus: false,    // ← ISSO resolve o problema da troca de aba
      refetchOnMount: false,          // ← não recarrega se dados já existem
      retry: 1,
    },
  },
});

// ✅ COMPONENTE PRINCIPAL (SEM ALTERAÇÕES NA ESTRUTURA)
function App() {
  const GOOGLE_CLIENT_ID = '861659089834-512mjhqe5t86fsbverlnk5ihjbeho5un.apps.googleusercontent.com';

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
          <AuthProvider>
            {/* ✅ NOVO: FeaturesProvider logo após AuthProvider */}
            <FeaturesProvider>
              <CartProvider>
                <ClipboardProvider>
                  <Router>
                    <AppContent />
                  </Router>
                </ClipboardProvider>
              </CartProvider>
            </FeaturesProvider>
          </AuthProvider>
        </GoogleOAuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;